import * as React from 'react'
import { cva } from 'class-variance-authority'
import { ChevronDownIcon } from '@/src/icons'
import { NavigationMenu as NavigationMenuPrimitive } from '@base-ui/react/navigation-menu'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// NavigationMenu — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Structural changes:
//   Radix: Root (renders div) > List > Item > Trigger + Content
//          Viewport (shared animated container for content)
//          Indicator (animated arrow under active trigger)
//   Base UI: Root (renders nav) > List > Item > Trigger + Content
//            Portal > Positioner > Popup > Viewport (for positioned content)
//            Icon (animated chevron indicator on trigger)
//
// Key differences:
//   - Radix NavigationMenu.Root renders a div; Base UI renders a <nav>
//   - Radix Viewport is a sibling of the List; Base UI uses Positioner > Popup > Viewport
//   - Radix Indicator is an animated arrow below the active trigger
//   - Base UI provides NavigationMenu.Icon for the chevron animation
//   - Radix uses data-[state=open/closed]; Base UI uses data-[open]/data-[starting-style]/data-[ending-style]
//   - Radix uses data-[motion=from-start/from-end/to-start/to-end] for content slide
//     Base UI Content has activationDirection state ('left'|'right'|'up'|'down'|null)
//   - Radix Link uses data-[active=true]; Base UI Link uses the `active` prop
//
// CSS variable renames:
//   --radix-navigation-menu-viewport-height → --popup-height (on Popup)
//   --radix-navigation-menu-viewport-width  → --popup-width  (on Popup)
//   Positioner also exposes --positioner-width/--positioner-height
//
// Known regression (Base UI migration):
//   Radix's NavigationMenu.Indicator rendered a position-tracking animated
//   arrow that slid beneath the active trigger. Base UI has no direct
//   equivalent. The visual caret/chevron on each trigger is retained via
//   Base UI's NavigationMenu.Icon, but the shared animated indicator bar
//   that tracked the active trigger position is lost. This is a minor
//   cosmetic regression with no functional or accessibility impact.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// NavigationMenu (Root)
// ---------------------------------------------------------------------------
// The `viewport` prop controls whether to render the Portal > Positioner > Popup > Viewport
// structure (default: true) or let Content render inline (viewport: false).

function NavigationMenu({
  className,
  children,
  viewport = true,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root> & {
  viewport?: boolean
}) {
  return (
    <NavigationMenuPrimitive.Root
      data-slot="navigation-menu"
      data-viewport={viewport}
      className={cn('group/navigation-menu relative flex max-w-max flex-1 items-center justify-center', className)}
      {...props}
    >
      {children}
      {viewport && <NavigationMenuViewportContainer />}
    </NavigationMenuPrimitive.Root>
  )
}

// ---------------------------------------------------------------------------
// NavigationMenuList
// ---------------------------------------------------------------------------

function NavigationMenuList({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
  return (
    <NavigationMenuPrimitive.List
      data-slot="navigation-menu-list"
      className={cn('group flex flex-1 list-none items-center justify-center gap-1', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// NavigationMenuItem
// ---------------------------------------------------------------------------

function NavigationMenuItem({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) {
  return (
    <NavigationMenuPrimitive.Item data-slot="navigation-menu-item" className={cn('relative', className)} {...props} />
  )
}

// ---------------------------------------------------------------------------
// navigationMenuTriggerStyle (CVA)
// ---------------------------------------------------------------------------

const navigationMenuTriggerStyle = cva(
  // DS: nav items use h-9, text-sm, font-medium. Hover uses accent fill; selected uses accent fill +
  // primary text colour per the official DS state spec (default / focused / hovered / selected).
  'group inline-flex h-9 w-max items-center justify-center gap-1.5 rounded-md bg-transparent px-3 py-2 text-sm font-medium transition-[color,box-shadow] outline-none hover:bg-accent hover:text-foreground focus:bg-accent focus:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[popup-open]:bg-accent/50 data-[popup-open]:text-foreground data-[popup-open]:hover:bg-accent data-[popup-open]:focus:bg-accent data-[active]:bg-accent data-[active]:text-primary data-[active]:hover:bg-accent data-[active]:hover:text-primary data-[active]:focus:bg-accent data-[active]:focus:text-primary',
)

// ---------------------------------------------------------------------------
// NavigationMenuTrigger
// ---------------------------------------------------------------------------

function NavigationMenuTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger>) {
  return (
    <NavigationMenuPrimitive.Trigger
      data-slot="navigation-menu-trigger"
      className={cn(navigationMenuTriggerStyle(), 'group', className)}
      {...props}
    >
      {children} {/* DS: icon-md (20px) for navigation menu chevrons per icon-default-size policy */}
      <ChevronDownIcon
        className="relative top-[1px] ml-0.5 size-5 shrink-0 transition duration-300 group-data-[popup-open]:rotate-180"
        aria-hidden="true"
      />
    </NavigationMenuPrimitive.Trigger>
  )
}

// ---------------------------------------------------------------------------
// NavigationMenuContent
// ---------------------------------------------------------------------------

function NavigationMenuContent({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.Content>) {
  return (
    <NavigationMenuPrimitive.Content
      data-slot="navigation-menu-content"
      className={cn(
        // When viewport=true (default): content is inside Portal > Positioner > Popup > Viewport.
        // Uses w-max so the Popup auto-sizes to the content width. h-full fills the viewport.
        // Fade transition on content while Popup handles the scale animation.
        'p-2 pr-2.5 transition-opacity duration-150 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 md:w-max md:h-full',
        // When viewport=false: content renders inline below the trigger with its own chrome.
        'group-data-[viewport=false]/navigation-menu:absolute group-data-[viewport=false]/navigation-menu:top-full group-data-[viewport=false]/navigation-menu:left-0 group-data-[viewport=false]/navigation-menu:mt-1.5 group-data-[viewport=false]/navigation-menu:overflow-hidden group-data-[viewport=false]/navigation-menu:rounded-md group-data-[viewport=false]/navigation-menu:border group-data-[viewport=false]/navigation-menu:bg-popover group-data-[viewport=false]/navigation-menu:text-popover-foreground group-data-[viewport=false]/navigation-menu:shadow group-data-[viewport=false]/navigation-menu:duration-200 **:data-[slot=navigation-menu-link]:focus:ring-0 **:data-[slot=navigation-menu-link]:focus:outline-none group-data-[viewport=false]/navigation-menu:data-[ending-style]:animate-out group-data-[viewport=false]/navigation-menu:data-[ending-style]:fade-out-0 group-data-[viewport=false]/navigation-menu:data-[ending-style]:zoom-out-95 group-data-[viewport=false]/navigation-menu:data-[starting-style]:animate-in group-data-[viewport=false]/navigation-menu:data-[starting-style]:fade-in-0 group-data-[viewport=false]/navigation-menu:data-[starting-style]:zoom-in-95',
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// NavigationMenuViewport — internal container that wraps Portal > Positioner > Popup > Viewport
// ---------------------------------------------------------------------------
// We keep this as a public export (`NavigationMenuViewport`) for backwards compat,
// but the NavigationMenu root auto-includes it when viewport=true.

function NavigationMenuViewportContainer() {
  return (
    <NavigationMenuPrimitive.Portal>
      <NavigationMenuPrimitive.Positioner
        data-slot="navigation-menu-positioner"
        side="bottom"
        sideOffset={6}
        align="start"
        className="w-[var(--positioner-width)] h-[var(--positioner-height)] max-w-[var(--available-width)] transition-[top,left,right,bottom] duration-200 data-[instant]:transition-none"
      >
        <NavigationMenuPrimitive.Popup
          data-slot="navigation-menu-popup"
          className="relative overflow-visible origin-[var(--transform-origin)] w-[var(--popup-width)] h-[var(--popup-height)] rounded-md border bg-popover text-popover-foreground shadow transition-[opacity,scale,width,height] duration-200 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[ending-style]:duration-150"
        >
          <NavigationMenuPrimitive.Viewport
            data-slot="navigation-menu-viewport"
            className="relative overflow-hidden w-full h-full"
          />
        </NavigationMenuPrimitive.Popup>
      </NavigationMenuPrimitive.Positioner>
    </NavigationMenuPrimitive.Portal>
  )
}

function NavigationMenuViewport({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) {
  return (
    <div className={cn('absolute top-full left-0 isolate z-50 flex justify-center')}>
      <NavigationMenuPrimitive.Viewport
        data-slot="navigation-menu-viewport"
        className={cn('relative overflow-hidden w-full h-full', className)}
        {...props}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// NavigationMenuLink
// ---------------------------------------------------------------------------

function NavigationMenuLink({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.Link>) {
  return (
    <NavigationMenuPrimitive.Link
      data-slot="navigation-menu-link"
      className={cn(
        "flex flex-col gap-1 rounded-sm p-2 text-sm transition-all outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 data-[active]:bg-accent data-[active]:text-primary data-[active]:hover:bg-accent data-[active]:hover:text-primary data-[active]:focus:bg-accent data-[active]:focus:text-primary [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// NavigationMenuIndicator
// ---------------------------------------------------------------------------
// Base UI does not have a direct equivalent to Radix's NavigationMenu.Indicator.
// We render a plain div styled the same way. The arrow caret animation is lost,
// but the visual style is preserved. If Base UI adds an Indicator primitive in
// future, we can swap it in.

function NavigationMenuIndicator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="navigation-menu-indicator"
      className={cn('top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden', className)}
      {...props}
    >
      <div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" />
    </div>
  )
}

export {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
}
